// What the lists and the game page share: a game's row, its badges and the
// figure each tag ranks it by, and cards of a few games (a tag's, a
// studio's, those ranked near). Nothing here touches the DOM.

import type { AppTag, RankedRow } from "../../shared/api.ts";
import { numberOrNull } from "../../shared/guards.ts";
import { appPath, tagPagePath } from "../../shared/site.ts";
import { changeFigure, changeNote, date, moveNote, number, peakNote } from "./figures.ts";
import { type Row, art, displayName } from "./markup.ts";
import { percentOff, priceMarkup } from "./store-facts.ts";
import { TAG_PAGE_NAMES, type TagPageName, type TagPageValue, tagPageValue } from "./pages/tag-pages.ts";
import { WAYS, escapeHtml, wayOf } from "./web.ts";

// A game's daily averages over the last week (a list row's `trend`) as a
// small line, coloured by which way the week went, in whole percent. An empty
// cell for fewer than two days.
function trendLine(values: readonly number[] | undefined): string {
  const first = values?.[0];
  const last = values?.at(-1);
  if (!values || values.length < 2 || first === undefined || last === undefined) {
    return `<span class="trend"></span>`;
  }

  const width = 100;
  const height = 28;
  const pad = 2;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const y = (v: number): number => (max === min ? height / 2 : pad + (height - 2 * pad) * (1 - (v - min) / (max - min)));
  const points = values
    .map((v, i) => `${((i * width) / (values.length - 1)).toFixed(1)},${y(v).toFixed(1)}`)
    .join(" ");

  // From zero there's no percentage, only a direction.
  const way = first > 0 ? wayOf(Math.round((last / first - 1) * 100)) : wayOf(last - first);

  return `<span class="trend ${way}" title="Daily average players, last 7 days" aria-hidden="true">
      <svg viewBox="0 0 ${width} ${height}" preserveAspectRatio="none"><polyline points="${points}"
        vector-effect="non-scaling-stroke"/></svg></span>`;
}

// A game as its badges are read from: a row, or the game a page is about.
type Tagged = { active?: boolean; tags: readonly AppTag[] };

// A badge as badgeTags gives it: its tag and value, and its text made.
type Badge = { tag: TagPageName; value: string; text: string } & Omit<NonNullable<TagPageValue["badge"]>, "text">;

// The game's badges (TAG_PAGES' values with one), as { tag, value, text, way,
// about }, in the registry's order whatever order the API sends them; none
// for an inactive game, whose tags would be about figures it no longer has.
function badgeTags(game: Tagged): Badge[] {
  if (game.active === false) return [];

  return TAG_PAGE_NAMES.flatMap((tag) => {
    const tagged = (game.tags ?? []).find((t) => t.tag === tag)?.value;
    const value = tagged?.state;
    const badge = value === undefined ? undefined : tagPageValue(tag, value)?.badge;
    if (value === undefined || !tagged || !badge) return [];
    const text = typeof badge.text === "function" ? badge.text(tagged) : badge.text;
    return [{ tag, value, ...badge, text }];
  });
}

// The game page's badges, under its title, each a link to its tag page.
// Each holds its meaning in a tip of our own, shown at once on hover or
// focus (a title waits about a second; on a touch screen, app.js makes a
// first tap focus it), and is described by it for screen readers.
export function moverBadges(game: Tagged): string {
  const badges = badgeTags(game).filter(({ onGamePage }) => onGamePage !== false).map(
    ({ tag, value, text, way, about }) => `<li><a class="badge ${way}"
        href="${tagPagePath(tag, value)}" data-nav aria-describedby="badge-${tag}">${WAYS[way]}${text}<span
          class="badge-tip" id="badge-${tag}" role="tooltip">${about}</span></a></li>`
  );

  return badges.length
    ? `<ul class="badges" aria-label="Standing out right now">${badges.join("")}</ul>`
    : "";
}

// A list row's badges, under the game's name: smaller, and with no tips or
// links, as the row is a link already. None for `skip`, a tag.
function rowBadges(game: Row, skip: TagPageName | null = null): string {
  const badges = badgeTags(game).filter(({ tag }) => tag !== skip).map(
    ({ text, way }) => `<span class="badge ${way}">${WAYS[way]}${text}</span>`
  );

  return badges.length ? `<span class="row-badges">${badges.join("")}</span>` : "";
}

// A card of games (a tag's, a company's): its `title` (HTML) linking to
// `path`, an "All" link there (`more`, read as `moreLabel`), a `blurb`
// (HTML) and `games` (cardGames'), in the three parts style.css's .tag-card
// lines up with the card beside it.
export const card = ({
  path,
  title,
  more,
  moreLabel,
  blurb,
  games
}: {
  path: string;
  title: string;
  more: string;
  moreLabel: string;
  blurb: string;
  games: string;
}): string => `
      <li class="panel tag-card">
        <div class="tag-card-head">
          <h3><a href="${path}" data-nav>${title}</a></h3>
          <a class="tag-card-more" href="${path}" data-nav
             aria-label="${moreLabel}">${more}</a>
        </div>
        <p class="muted">${blurb}</p>
        ${games}
      </li>`;

// A game's row. A search result can be unranked, or have no published
// count yet, so the rank is left blank and the count shown as "—" rather
// than "undefined" and "0". `sub` is the figures under the count (a tag
// page's are its tag's), and `ownTag` a tag whose badge the row leaves off:
// on its own page every row would carry it.
export function appRow(
  game: Row,
  sub = changeNote(game) + peakNote(game.player_count, game.peak_player_count, true),
  ownTag: TagPageName | null = null
): string {
  const name = displayName(game);

  return `
      <li>
        <a class="app-row" href="${appPath(game.app_id)}" data-nav>
          <span class="rank">${game.rank ?? ""}${moveNote(game)}</span>
          ${art(game, name)}
          <span class="name-cell">
            <span class="name">${escapeHtml(name)}</span>${rowBadges(game, ownTag)}
          </span>${trendLine(game.trend)}
          <span class="count">
            <span>${
            game.player_count == null ? "—" : number.format(game.player_count)
          }</span>${sub && ` <span class="sub">${sub}</span>`}
          </span>
        </a>
      </li>`;
}

// The figure each tag ranks by, as a tag card's and a tag page's rows show
// it with the count. update_bump's is the rise after its update,
// sale_bump's the rise during its sale with the discount, on_sale's the
// price struck through beside the sale price, and new_release's the day it
// came out. busy_hours' is its tag strength: how many times its average
// hour its best evening runs, or, with no evening peak, how flat its day
// is; none until the tagger's next weekly run has scored it.
// A tag's value from the API is its JSON: each field is checked.
export const tagFigures: Readonly<Record<TagPageName, (game: Row) => string>> = {
  trend: changeNote,
  rank: (game) => moveNote(game, true),
  peak_share: (game) => peakNote(game.player_count, game.peak_player_count),
  update_bump: (game) => changeFigure(numberOrNull(game.value?.value), "after update"),
  sale_bump: (game) => {
    const discount = numberOrNull(game.value?.discount);
    return (
      changeFigure(numberOrNull(game.value?.value), "during sale") +
      (discount == null ? "" : `<small>${percentOff(discount)}</small>`)
    );
  },
  on_sale: (game) => {
    const initial = numberOrNull(game.value?.initial);
    const final = numberOrNull(game.value?.final);
    const discount = numberOrNull(game.value?.value);
    return initial == null || final == null || discount == null
      ? ""
      : `<small class="price-figure">${priceMarkup(initial, final, discount)}</small>`;
  },
  new_release: (game) => {
    const released = numberOrNull(game.value?.released);
    return released == null
      ? ""
      : `<small class="change"><span>released</span> ${date(released)}</small>`;
  },
  busy_hours: (game) => {
    const value = numberOrNull(game.value?.value);
    const peakHour = game.value?.peakHour;
    return value == null
      ? ""
      : peakHour == null
        ? `<small class="change">${Math.round(value * 100)}% <span>flat</span></small>`
        : `<small class="change">${value.toFixed(1)}× <span>avg hour</span></small>`;
  }
};

// A card's games, as small rows: art, name, count and `figure(game)`.
// With `current` (an app id, for the games ranked near it), each name has
// its rank before it and that game's row is marked as this page's. Nothing
// for none.
export function cardGames(
  apps: readonly RankedRow[] | undefined,
  figure: (game: RankedRow) => string,
  current: number | null = null
): string {
  if (!apps?.length) return "";

  return `<div class="tag-card-games"><ol>${apps
    .map((game) => {
      const name = displayName(game);
      const rank = current != null && game.rank != null
        ? `<span class="rank">${game.rank}</span> `
        : "";
      return `
            <li>
              <a class="tag-card-row" href="${appPath(game.app_id)}" data-nav${
              game.app_id === current ? ' aria-current="page"' : ""}>
                ${art(game, name)}
                <span class="name">${rank}${escapeHtml(name)}</span>
                <span class="count"><span>${number.format(
                game.player_count)}</span>${figure(game)}</span>
              </a>
            </li>`;
    })
    .join("")}</ol></div>`;
}

// One of page 0's tag cards: the tag page's title, linking to it, its blurb
// and its top games as small rows: three parts, which style.css's
// .tag-card lines up with the card beside it. Nothing when no game has the
// tag right now. With `keep` (the tags index), a tag with no games right
// now still gets its card, as a link to its page.
export function tagCard(tag: TagPageName, value: string, apps: readonly RankedRow[] | undefined, keep = false): string {
  const def = tagPageValue(tag, value);
  if ((!apps?.length && !keep) || !def) return "";
  const { title, short, text } = def;
  // The tags index explains each tag in full; the home page keeps it short.
  const blurb = keep ? text : short;
  const path = tagPagePath(tag, value);

  return card({
    path,
    title,
    more: "All →",
    moreLabel: `All ${title.toLowerCase()}`,
    blurb,
    games: cardGames(apps, tagFigures[tag])
  });
}

